import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { brandAssets } from '../data/mock';
import { Menu, X, ChevronDown, ChevronRight } from 'lucide-react';

// Diagnostics sub-menu items (the twelve instruments, grouped, each linking to its product page)
const diagnosticsLinks = [
  { label: "Overview", href: "/diagnostics" },
  { heading: "People" },
  { label: "DISC Lens™", href: "/product/disc-lens" },
  { label: "DISC Lens Africa™", href: "/product/disc-africa" },
  { label: "EI Lens™", href: "/product/ei-lens" },
  { label: "PurposeMap™", href: "/product/purposemap" },
  { label: "InsightEdge™", href: "/product/insightedge" },
  { label: "Impact 360™", href: "/product/impact-360" },
  { label: "Leadership Mirror™", href: "/product/leadership-mirror" },
  { label: "Executive Mirror™", href: "/product/executive-mirror" },
  { label: "JobFit™", href: "/product/jobfit" },
  { heading: "Teams and organisations" },
  { label: "DISC Lens Teams™", href: "/product/disc-africa-teams" },
  { label: "TeamEdge™", href: "/product/team-edge" },
  { label: "CulturePulse™", href: "/product/culture-pulse" },
  { label: "Partner Assessments", href: "/partner-assessments" }
];

// Core Domains dropdown items with nested structure
const coreDomainsLinks = [
  { 
    label: "Diagnostics & Insights", 
    href: "/diagnostics",
    hasSubmenu: true,
    submenu: diagnosticsLinks
  },
  { label: "Coaching & Leadership Development", href: "/coaching" },
  { label: "Capability Building and Training", href: "/capability-building" },
  { label: "Advisory & Organisational Change", href: "/advisory" },
  { label: "Career Selection & Assessment Centres", href: "/early-career-selection" },
  { label: "Leadership & Succession Intelligence", href: "/leadership-succession-intelligence" }
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showCoreDomainsDropdown, setShowCoreDomainsDropdown] = useState(false);
  const [showDiagnosticsSubmenu, setShowDiagnosticsSubmenu] = useState(false);
  const [mobileCoreDomainsOpen, setMobileCoreDomainsOpen] = useState(false);
  const [mobileDiagnosticsOpen, setMobileDiagnosticsOpen] = useState(false);
  const location = useLocation();
  
  // Helper function to check if a path is active
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };
  
  // Check if any core domain is active
  const isCoreDomainsActive = () => {
    const coreDomainPaths = ['/diagnostics', '/disc-africa', '/leadership-mirror', '/culturepulse', '/partner-assessments', '/coaching', '/capability-building', '/advisory', '/early-career-selection', '/leadership-succession-intelligence'];
    return coreDomainPaths.some(path => location.pathname.startsWith(path));
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-white/10" role="banner">
      <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
        {/* Logo */}
        <a href="/" className="flex items-center" aria-label="RESILIGENT - Go to homepage">
          <img 
            src={brandAssets.logoWhite} 
            alt="RESILIGENT - Leadership Development Nigeria" 
            className="h-9 md:h-10 w-auto"
          />
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-6" aria-label="Main navigation">
          {/* About */}
          <a
            href="/about"
            className={`text-sm font-medium transition-colors duration-300 ${isActive('/about') ? 'text-[#2693d0]' : 'text-white/80 hover:text-white'}`}
            aria-current={isActive('/about') ? 'page' : undefined}
          >
            About
          </a>

          {/* Our People */}
          <a
            href="/our-people"
            className={`text-sm font-medium transition-colors duration-300 ${isActive('/our-people') ? 'text-[#2693d0]' : 'text-white/80 hover:text-white'}`}
            aria-current={isActive('/our-people') ? 'page' : undefined}
          >
            Our People
          </a>

          {/* How We Work */}
          <a
            href="/engagement"
            className={`text-sm font-medium transition-colors duration-300 ${isActive('/engagement') ? 'text-[#2693d0]' : 'text-white/80 hover:text-white'}`}
          >
            How We Work
          </a>

          {/* Core Domains Dropdown */}
          <div 
            className="relative"
            onMouseEnter={() => setShowCoreDomainsDropdown(true)}
            onMouseLeave={() => {
              setShowCoreDomainsDropdown(false);
              setShowDiagnosticsSubmenu(false);
            }}
          >
            <button className={`flex items-center gap-1 text-sm font-medium transition-colors duration-300 ${isCoreDomainsActive() ? 'text-[#2693d0]' : 'text-white/80 hover:text-white'}`}>
              Core Domains
              <ChevronDown size={14} className={`transition-transform duration-200 ${showCoreDomainsDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showCoreDomainsDropdown && (
              <div className="absolute top-full left-0 pt-2">
                <div className="bg-slate-800 rounded-lg shadow-xl border border-slate-700 overflow-visible min-w-[320px] z-50">
                  {coreDomainsLinks.map((domain, index) => (
                    <div 
                      key={domain.label}
                      className="relative"
                      onMouseEnter={() => domain.hasSubmenu && setShowDiagnosticsSubmenu(true)}
                      onMouseLeave={() => domain.hasSubmenu && setShowDiagnosticsSubmenu(false)}
                    >
                      <a
                        href={domain.href}
                        className={`flex items-center justify-between px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-slate-700 transition-colors duration-200 ${index > 0 ? 'border-t border-slate-700' : ''}`}
                      >
                        {domain.label}
                        {domain.hasSubmenu && <ChevronRight size={14} className="text-white/50" />}
                      </a>
                      
                      {/* Nested Submenu for Diagnostics */}
                      {domain.hasSubmenu && showDiagnosticsSubmenu && (
                        <div className="absolute left-full top-0 pl-2">
                          <div className="bg-slate-800 rounded-lg shadow-xl border border-slate-700 overflow-y-auto max-h-[80vh] min-w-[240px]">
                            {domain.submenu.map((subItem) => (
                              subItem.heading ? (
                                <div
                                  key={subItem.heading}
                                  className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-white/40 border-t border-slate-700"
                                >
                                  {subItem.heading}
                                </div>
                              ) : (
                                <a
                                  key={subItem.label}
                                  href={subItem.href}
                                  className="block px-4 py-2.5 text-sm text-white/80 hover:text-white hover:bg-slate-700 transition-colors duration-200"
                                >
                                  {subItem.label}
                                </a>
                              )
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Case Studies & Clients */}
          <a
            href="/case-studies"
            className={`text-sm font-medium transition-colors duration-300 ${isActive('/case-studies') ? 'text-[#2693d0]' : 'text-white/80 hover:text-white'}`}
          >
            Case Studies
          </a>

          {/* Impact AS Plus™ - Single Page Link */}
          <a
            href="/impact-as-plus"
            className={`text-sm font-medium transition-colors duration-300 ${isActive('/impact-as-plus') ? 'text-[#2693d0]' : 'text-white/80 hover:text-white'}`}
          >
            Impact AS Plus™
          </a>
        </nav>

        {/* CTA Buttons */}
        <div className="hidden lg:flex items-center gap-3">
          {/* Get in Touch - Primary CTA */}
          <a
            href="/contact"
            className="bg-[#2693d0] text-white px-4 py-2 rounded text-sm font-medium hover:bg-[#1e7ab8] transition-all duration-300"
          >
            Get in Touch
          </a>
          
          {/* Portal Login - Secondary Utility */}
          <a
            href="/impact-as-plus-portal"
            className="text-white/70 text-sm font-medium hover:text-white transition-colors duration-300 border border-white/20 px-4 py-2 rounded hover:border-white/40"
          >
            Portal Login
          </a>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="lg:hidden text-white p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#0a0a0a] border-t border-white/10 px-6 py-6">
          <nav className="flex flex-col gap-4">
            {/* About */}
            <a
              href="/about"
              className="text-white text-base font-medium py-2 hover:text-[#2693d0] transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              About
            </a>

            {/* Our People */}
            <a
              href="/our-people"
              className="text-white text-base font-medium py-2 hover:text-[#2693d0] transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Our People
            </a>

            {/* How We Work */}
            <a
              href="/engagement"
              className="text-white text-base font-medium py-2 hover:text-[#2693d0] transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              How We Work
            </a>

            {/* Core Domains - Mobile Accordion */}
            <div className="flex flex-col">
              <button
                className="flex items-center justify-between text-white text-base font-medium py-2"
                onClick={() => setMobileCoreDomainsOpen(!mobileCoreDomainsOpen)}
              >
                Core Domains
                <ChevronDown size={18} className={`transition-transform duration-200 ${mobileCoreDomainsOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileCoreDomainsOpen && (
                <div className="flex flex-col gap-1 mt-1">
                  {coreDomainsLinks.map((domain) => (
                    <div key={domain.label}>
                      {domain.hasSubmenu ? (
                        <div className="flex flex-col">
                          <button
                            className="flex items-center justify-between text-white/70 text-sm font-medium py-2 pl-4 hover:text-[#2693d0] transition-colors"
                            onClick={() => setMobileDiagnosticsOpen(!mobileDiagnosticsOpen)}
                          >
                            {domain.label}
                            <ChevronDown size={14} className={`transition-transform duration-200 ${mobileDiagnosticsOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {mobileDiagnosticsOpen && (
                            <div className="flex flex-col gap-1 mt-1">
                              {domain.submenu.map((subItem) => (
                                subItem.heading ? (
                                  <div
                                    key={subItem.heading}
                                    className="text-white/40 text-[11px] font-semibold uppercase tracking-wider pt-3 pb-1 pl-8"
                                  >
                                    {subItem.heading}
                                  </div>
                                ) : (
                                  <a
                                    key={subItem.label}
                                    href={subItem.href}
                                    className="text-white/60 text-sm font-medium py-2 pl-8 hover:text-[#2693d0] transition-colors"
                                    onClick={() => setMobileMenuOpen(false)}
                                  >
                                    {subItem.label}
                                  </a>
                                )
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <a
                          href={domain.href}
                          className="text-white/70 text-sm font-medium py-2 pl-4 hover:text-[#2693d0] transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {domain.label}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Case Studies & Clients */}
            <a
              href="/case-studies"
              className="text-white text-base font-medium py-2 hover:text-[#2693d0] transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Case Studies
            </a>

            {/* Impact AS Plus™ */}
            <a
              href="/impact-as-plus"
              className="text-white text-base font-medium py-2 hover:text-[#2693d0] transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Impact AS Plus™
            </a>

            {/* CTAs */}
            <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-white/10">
              <a
                href="/contact"
                className="bg-[#2693d0] text-white px-5 py-3 rounded font-medium text-sm text-center hover:bg-[#1e7ab8] transition-all"
                onClick={() => setMobileMenuOpen(false)}
              >
                Get in Touch
              </a>
              <a
                href="/impact-as-plus-portal"
                className="text-white/70 text-sm font-medium text-center py-3 border border-white/20 rounded hover:text-white hover:border-white/40 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Portal Login
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
